import { Grid } from '@material-ui/core';
import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import ReactCrop, { PixelCrop } from 'react-image-crop';
import { setCanvasPreview } from './common';
import 'react-image-crop/dist/ReactCrop.css';

/**
 * Crop data interface for persisting crop coordinates
 * Used to store and restore crop state across edit sessions
 */
export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Props {
  image?: string;
  imageUrl?: string;
  onCropComplete: (dataUrl: string) => void;
  /** Called when crop coordinates change, for persisting crop data */
  onCropChange?: (cropData: CropData) => void;
  /** Initial crop coordinates to restore from previous edit session */
  initialCrop?: CropData | null;
  style?: HTMLAttributes<HTMLCanvasElement>;
  aspect?: number;
}

const DEFAULT_CROP_SIZE = 200;

const ImageCropper = ({ image, imageUrl, onCropComplete, onCropChange, initialCrop, style, aspect = 1 }: Props) => {
  // Use initialCrop if provided, otherwise use default centered crop
  const getInitialCrop = (): PixelCrop => {
    if (initialCrop && initialCrop.width > 0 && initialCrop.height > 0) {
      return {
        unit: 'px',
        x: initialCrop.x,
        y: initialCrop.y,
        width: initialCrop.width,
        height: initialCrop.height,
      };
    }
    return {
      unit: 'px',
      x: 0,
      y: 0,
      width: DEFAULT_CROP_SIZE,
      height: DEFAULT_CROP_SIZE,
    };
  };

  const [crop, setCrop] = useState<PixelCrop>(getInitialCrop());
  const [hasAppliedInitialCrop, setHasAppliedInitialCrop] = useState(false);

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Apply initial crop when image loads (for existing images with saved crop data)
  const handleImageLoad = () => {
    if (initialCrop && initialCrop.width > 0 && initialCrop.height > 0 && !hasAppliedInitialCrop) {
      const initialPixelCrop: PixelCrop = {
        unit: 'px',
        x: initialCrop.x,
        y: initialCrop.y,
        width: initialCrop.width,
        height: initialCrop.height,
      };
      setCrop(initialPixelCrop);
      setHasAppliedInitialCrop(true);
      // Trigger initial crop complete to generate preview
      handleComplete(initialPixelCrop);
    }
  };

  // Update crop state when initialCrop prop changes
  useEffect(() => {
    if (initialCrop && initialCrop.width > 0 && initialCrop.height > 0) {
      setCrop({
        unit: 'px',
        x: initialCrop.x,
        y: initialCrop.y,
        width: initialCrop.width,
        height: initialCrop.height,
      });
    }
  }, [initialCrop]);

  const handleComplete = (p: PixelCrop) => {
    if (imageRef.current && previewCanvasRef.current) {
      setCanvasPreview(imageRef.current, previewCanvasRef.current, p);
      const dataUrl = previewCanvasRef.current!.toDataURL('image/jpeg', 0.9);
      onCropComplete(dataUrl);

      // Notify parent of crop coordinate changes for persistence
      if (onCropChange) {
        onCropChange({
          x: p.x,
          y: p.y,
          width: p.width,
          height: p.height,
        });
      }
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ReactCrop crop={crop} onChange={setCrop} aspect={aspect} minHeight={200} onComplete={(p) => handleComplete(p)}>
          <img crossOrigin="anonymous" src={image ? image : imageUrl} ref={imageRef} alt="cropper" onLoad={handleImageLoad} />
        </ReactCrop>
      </Grid>
      {crop && (
        <canvas
          ref={previewCanvasRef}
          className="mt-4"
          style={
            style
              ? style
              : {
                  display: 'none',
                  border: '1px solid black',
                  objectFit: 'contain',
                  width: 150,
                  height: 150,
                }
          }
        />
      )}
    </Grid>
  );
};

export default ImageCropper;
